import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { Cookie, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { language } = useLanguage();

  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('gaius-cookie-consent');
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const save = (prefs: typeof preferences) => {
    localStorage.setItem('gaius-cookie-consent', JSON.stringify({ ...prefs, date: new Date().toISOString() }));
    setVisible(false);
  };

  const acceptAll = () => save({ necessary: true, functional: true, analytics: true, marketing: true });
  const acceptSelected = () => save(preferences);
  const rejectAll = () => save({ necessary: true, functional: false, analytics: false, marketing: false });

  if (!visible) return null;

  const fr = language === 'fr';

  const categories = [
    {
      id: 'necessary' as const,
      label: fr ? 'Cookies strictement nécessaires' : 'Strictly necessary cookies',
      desc: fr
        ? 'Ces cookies sont indispensables au fonctionnement du site (navigation, sécurité, authentification). Ils ne peuvent pas être désactivés.'
        : 'These cookies are essential for the website to function (navigation, security, authentication). They cannot be disabled.',
      locked: true,
    },
    {
      id: 'functional' as const,
      label: fr ? 'Cookies fonctionnels' : 'Functional cookies',
      desc: fr
        ? 'Permettent de mémoriser vos préférences (langue, région) et d\'améliorer votre confort de navigation.'
        : 'Allow us to remember your preferences (language, region) and improve your browsing comfort.',
      locked: false,
    },
    {
      id: 'analytics' as const,
      label: fr ? 'Cookies de mesure d\'audience' : 'Analytics cookies',
      desc: fr
        ? 'Nous aident à comprendre comment les visiteurs utilisent le site grâce à des statistiques anonymes (pages consultées, durée de visite).'
        : 'Help us understand how visitors use the site through anonymous statistics (pages viewed, visit duration).',
      locked: false,
    },
    {
      id: 'marketing' as const,
      label: fr ? 'Cookies publicitaires et de ciblage' : 'Advertising & targeting cookies',
      desc: fr
        ? 'Utilisés pour afficher des contenus et publicités pertinents en fonction de vos centres d\'intérêt. Ils peuvent être déposés par des partenaires tiers.'
        : 'Used to display relevant content and ads based on your interests. They may be set by third-party partners.',
      locked: false,
    },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center pointer-events-none">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px] pointer-events-auto" />

      <div className="relative pointer-events-auto w-full max-w-2xl mx-4 mb-6 bg-card border border-border rounded-2xl shadow-2xl animate-in slide-in-from-bottom-8 duration-500 max-h-[85vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <Cookie className="h-5 w-5 text-primary" />
              </div>
              <h3 className="font-semibold text-foreground text-lg">
                {fr ? 'Gestion des cookies' : 'Cookie settings'}
              </h3>
            </div>
            <button onClick={rejectAll} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Description */}
          <p className="text-sm text-muted-foreground mb-2 leading-relaxed">
            {fr
              ? 'Lorsque vous naviguez sur notre site, des cookies peuvent être déposés sur votre terminal. Vous pouvez accepter ou refuser ces cookies, et modifier vos choix à tout moment.'
              : 'When you browse our site, cookies may be stored on your device. You can accept or refuse these cookies and change your choices at any time.'}
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            {fr ? 'Pour en savoir plus, consultez notre ' : 'For more information, see our '}
            <Link to={fr ? '/confidentialite' : '/privacy-policy'} className="underline underline-offset-2 hover:text-foreground transition-colors inline-flex items-center gap-1">
              {fr ? 'politique de confidentialité' : 'privacy policy'}
              <ExternalLink className="h-3 w-3" />
            </Link>.
          </p>

          {/* Detailed categories */}
          {showDetails && (
            <div className="mb-4 space-y-1 rounded-xl border border-border overflow-hidden text-sm">
              {categories.map((cat) => (
                <label
                  key={cat.id}
                  className={`flex items-start justify-between gap-4 p-4 bg-muted/30 even:bg-muted/60 ${cat.locked ? '' : 'cursor-pointer'}`}
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-foreground">{cat.label}</span>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{cat.desc}</p>
                  </div>
                  <div className="pt-0.5 flex-shrink-0">
                    {cat.locked ? (
                      <div className="h-5 w-9 rounded-full bg-primary/80 flex items-center justify-end px-0.5">
                        <div className="h-4 w-4 rounded-full bg-primary-foreground" />
                      </div>
                    ) : (
                      <button
                        type="button"
                        role="switch"
                        aria-checked={preferences[cat.id]}
                        onClick={() => setPreferences(p => ({ ...p, [cat.id]: !p[cat.id] }))}
                        className={`h-5 w-9 rounded-full transition-colors flex items-center px-0.5 ${preferences[cat.id] ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                      >
                        <div className={`h-4 w-4 rounded-full bg-primary-foreground transition-transform ${preferences[cat.id] ? 'translate-x-4' : 'translate-x-0'}`} />
                      </button>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Button onClick={acceptAll} className="flex-1 rounded-xl">
              {fr ? 'Tout accepter' : 'Accept all'}
            </Button>
            {showDetails ? (
              <Button onClick={acceptSelected} variant="secondary" className="flex-1 rounded-xl">
                {fr ? 'Enregistrer mes choix' : 'Save my choices'}
              </Button>
            ) : (
              <Button onClick={() => setShowDetails(true)} variant="secondary" className="flex-1 rounded-xl">
                {fr ? 'Personnaliser' : 'Customize'}
              </Button>
            )}
            <Button onClick={rejectAll} variant="ghost" className="flex-1 rounded-xl text-muted-foreground">
              {fr ? 'Tout refuser' : 'Reject all'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
